import { Controller } from "@hotwired/stimulus"

// Rich Text Editor Controller
// Provides basic WYSIWYG editing with toolbar and placeholder support
export default class extends Controller {
  static targets = ["editor", "input"]

  connect() {
    this.editorTarget.addEventListener('input', this.syncContent.bind(this))
    this.editorTarget.addEventListener('paste', this.handlePaste.bind(this))
    this.editorTarget.addEventListener('focus', this.handleFocus.bind(this))
    this.editorTarget.addEventListener('blur', this.handleBlur.bind(this))
    
    // Initialize empty state styling
    this.updatePlaceholderState()
  }

  disconnect() {
    this.editorTarget.removeEventListener('input', this.syncContent.bind(this))
    this.editorTarget.removeEventListener('paste', this.handlePaste.bind(this))
    this.editorTarget.removeEventListener('focus', this.handleFocus.bind(this))
    this.editorTarget.removeEventListener('blur', this.handleBlur.bind(this))
  }

  syncContent() {
    this.inputTarget.value = this.editorTarget.innerHTML
    this.updatePlaceholderState()
  }

  updatePlaceholderState() {
    const isEmpty = this.editorTarget.innerHTML.trim() === '' || 
                    this.editorTarget.innerHTML === '<br>' ||
                    this.editorTarget.textContent.trim() === ''
    
    this.editorTarget.classList.toggle('is-empty', isEmpty)
  }

  handleFocus() {
    this.editorTarget.classList.add('is-focused')
  }

  handleBlur() {
    this.editorTarget.classList.remove('is-focused')
    this.syncContent()
  }

  handlePaste(event) {
    event.preventDefault()
    const text = event.clipboardData.getData('text/html') || event.clipboardData.getData('text/plain')
    
    // Sanitize and insert
    const sanitized = this.sanitizeHtml(text)
    document.execCommand('insertHTML', false, sanitized)
    this.syncContent()
  }

  sanitizeHtml(html) {
    // Create a temporary element to parse HTML
    const temp = document.createElement('div')
    temp.innerHTML = html
    
    // Remove script tags and event handlers
    temp.querySelectorAll('script, style').forEach(el => el.remove())
    temp.querySelectorAll('*').forEach(el => {
      // Remove all attributes except href on links
      Array.from(el.attributes).forEach(attr => {
        if (attr.name.startsWith('on') || (attr.name !== 'href' && el.tagName !== 'A')) {
          el.removeAttribute(attr.name)
        }
      })
    })
    
    return temp.innerHTML
  }

  bold(event) {
    event.preventDefault()
    document.execCommand('bold', false, null)
    this.editorTarget.focus()
    this.syncContent()
  }

  italic(event) {
    event.preventDefault()
    document.execCommand('italic', false, null)
    this.editorTarget.focus()
    this.syncContent()
  }

  underline(event) {
    event.preventDefault()
    document.execCommand('underline', false, null)
    this.editorTarget.focus()
    this.syncContent()
  }

  insertLink(event) {
    event.preventDefault()
    const url = prompt('Enter URL:', 'https://')
    if (url) {
      document.execCommand('createLink', false, url)
      this.editorTarget.focus()
      this.syncContent()
    }
  }

  insertPlaceholder(event) {
    event.preventDefault()
    const placeholder = event.currentTarget.dataset.placeholder
    
    // Focus the editor
    this.editorTarget.focus()
    
    // Insert the placeholder at cursor position
    document.execCommand('insertText', false, placeholder)
    this.syncContent()
  }
}
